import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { inr, fmtDateTime, downloadFile, COMPANY_STYLES } from "@/api";
import { useAuth } from "@/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Check, X, FileDown, Copy, Share2, ArrowRightCircle } from "lucide-react";

const Q_STYLE = {
  "Pending Approval": "bg-warning-tint text-warning border-warning/30",
  Approved: "bg-success-tint text-success border-success/30",
  Rejected: "bg-danger-tint text-danger border-danger/30",
  Draft: "bg-muted text-muted-foreground border-border",
  Sent: "bg-info-tint text-info border-info/30",
  Viewed: "bg-primary-tint text-primary border-primary/30",
  Accepted: "bg-success/15 text-success border-success/40",
  Expired: "bg-muted text-muted-foreground border-border",
};

export default function Quotations() {
  const { user } = useAuth();
  const [quotes, setQuotes] = useState([]);
  const [tab, setTab] = useState("Pending Approval");
  const [rejectFor, setRejectFor] = useState(null);
  const [reason, setReason] = useState("");
  const canApprove = ["super_admin", "admin"].includes(user.role);

  const load = useCallback(() => api.get("/quotations").then((r) => setQuotes(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const approve = async (q) => {
    try {
      const r = await api.post(`/quotations/${q.id}/approve`);
      toast.success(`Approved — Job ${r.data.job.job_no} created`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const reject = async () => {
    try {
      await api.post(`/quotations/${rejectFor.id}/reject`, { reason });
      toast.success("Quotation rejected (kept for history)");
      setRejectFor(null); setReason(""); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const duplicate = async (q) => {
    try {
      const r = await api.post(`/quotations/${q.id}/duplicate`);
      toast.success(`Duplicated as ${r.data.quote_no} (Rev ${r.data.revision_no})`); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };
  const share = async (q) => {
    try {
      const r = await api.post(`/quotations/${q.id}/share-link`);
      await navigator.clipboard.writeText(r.data.url);
      toast.success("Public link copied — customer can Accept/Reject online"); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };
  const convert = async (q) => {
    try {
      const r = await api.post(`/quotations/${q.id}/convert`);
      toast.success(r.data.job ? `Job ${r.data.job.job_no} created` : `Order ${r.data.order.order_no} created`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const filtered = tab === "All" ? quotes : quotes.filter((q) => q.status === tab);
  const summary = {
    total: quotes.length,
    pending: quotes.filter((q) => q.status === "Pending Approval").length,
    viewed: quotes.filter((q) => q.status === "Viewed").length,
    accepted: quotes.filter((q) => q.status === "Accepted").length,
  };

  return (
    <div className="space-y-5" data-testid="quotations-page">
      <div className="rounded-[28px] border border-border/70 bg-surface-elevated p-5 sm:p-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-[0.22em] text-primary">Approvals</p>
            <h1 className="font-heading text-2xl font-bold tracking-tight sm:text-3xl">Quotations & Approvals</h1>
            <p className="mt-1 text-sm text-muted-foreground">Approval converts automatically into a job</p>
          </div>
          <div className="inline-flex items-center rounded-full border border-border bg-background/80 px-3 py-1.5 text-xs font-medium text-muted-foreground">
            {quotes.filter((q) => q.status === "Pending Approval").length} waiting review
          </div>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {[
          { label: "Total quotes", value: summary.total, tone: "bg-primary-tint text-primary" },
          { label: "Pending", value: summary.pending, tone: "bg-warning-tint text-warning" },
          { label: "Viewed", value: summary.viewed, tone: "bg-info-tint text-info" },
          { label: "Accepted", value: summary.accepted, tone: "bg-success-tint text-success" },
        ].map((item) => (
          <Card key={item.label} className="p-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{item.label}</p>
                <div className="mt-3 text-2xl font-bold tabular-nums">{item.value}</div>
              </div>
              <span className={`flex h-11 w-11 items-center justify-center rounded-xl ${item.tone}`}>
                <span className="h-2.5 w-2.5 rounded-full bg-current opacity-80" />
              </span>
            </div>
          </Card>
        ))}
      </div>

      <Card className="p-2 sm:p-3">
        <div className="flex gap-2 flex-wrap">
          {["Pending Approval", "Sent", "Viewed", "Accepted", "Approved", "Rejected", "Draft", "All"].map((s) => (
            <button key={s} data-testid={`quote-tab-${s.toLowerCase().replace(/ /g, "-")}`} onClick={() => setTab(s)}
              className={`inline-flex items-center gap-1 rounded-full border px-3 py-1.5 text-xs font-medium transition-all ${tab === s ? "border-primary bg-primary text-primary-foreground shadow-sm" : "border-transparent bg-muted/60 text-foreground hover:bg-muted"}`}>
              {s}
              {s !== "All" && <span className="rounded-full bg-primary-tint/10 px-1.5 py-0.5 text-[10px] opacity-80">{quotes.filter((q) => q.status === s).length}</span>}
            </button>
          ))}
        </div>
      </Card>

      <Card className="overflow-hidden">
        <table className="w-full text-sm" data-testid="quotations-table">
          <thead className="bg-muted/60">
            <tr className="text-left text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
              <th className="px-4 py-3">Quote No</th><th className="px-4 py-3">Customer</th><th className="px-4 py-3">Brand</th>
              <th className="px-4 py-3">Service</th><th className="px-4 py-3 text-right">Amount</th><th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">By</th><th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((q) => (
              <tr key={q.id} data-testid={`quote-row-${q.id}`} className="border-t border-border/70 transition-colors hover:bg-accent/40">
                <td className="px-4 py-3 font-mono text-xs">{q.quote_no}</td>
                <td className="px-4 py-3"><div className="font-medium">{q.customer_name}</div><div className="text-xs text-muted-foreground">{q.phone}</div></td>
                <td className="px-4 py-3"><span className={`text-xs px-2 py-1 rounded-full border ${COMPANY_STYLES[q.company] || ""}`}>{q.company}</span></td>
                <td className="px-4 py-3 text-muted-foreground">{q.service}</td>
                <td className="px-4 py-3 text-right font-mono">{inr(q.amount)}</td>
                <td className="px-4 py-3"><span className={`text-xs px-2 py-1 rounded-full border ${Q_STYLE[q.status] || ""}`}>{q.status}</span>{q.revision_no > 1 && <span className="ml-1 text-[10px] text-muted-foreground font-mono">R{q.revision_no}</span>}</td>
                <td className="px-4 py-3 text-xs text-muted-foreground">{q.created_by_name}<br />{fmtDateTime(q.created_at)}</td>
                <td className="px-4 py-3">
                  <div className="flex gap-1.5 justify-end">
                    {canApprove && q.status === "Pending Approval" && (
                      <>
                        <Button size="sm" data-testid={`quote-approve-${q.id}`} onClick={() => approve(q)} className="h-8 bg-success text-inverse hover:bg-success/90"><Check size={14} /></Button>
                        <Button size="sm" variant="destructive" data-testid={`quote-reject-${q.id}`} onClick={() => setRejectFor(q)} className="h-8"><X size={14} /></Button>
                      </>
                    )}
                    {["Approved", "Accepted"].includes(q.status) && !q.converted_job_id && !q.converted_order_id && canApprove && (
                      <Button size="sm" variant="outline" data-testid={`quote-convert-${q.id}`} onClick={() => convert(q)} className="h-8 border-primary text-primary" title="Convert to Job/Order"><ArrowRightCircle size={13} /></Button>
                    )}
                    <Button size="sm" variant="outline" data-testid={`quote-duplicate-${q.id}`} onClick={() => duplicate(q)} className="h-8" title="Duplicate (new revision)"><Copy size={13} /></Button>
                    <Button size="sm" variant="outline" data-testid={`quote-share-${q.id}`} onClick={() => share(q)} className="h-8" title="Copy public accept/reject link"><Share2 size={13} /></Button>
                    <Button size="sm" variant="outline" data-testid={`quote-pdf-${q.id}`} onClick={() => downloadFile(`/quotations/${q.id}/pdf`, `${q.quote_no}.pdf`).catch(() => toast.error("PDF failed"))} className="h-8"><FileDown size={14} /></Button>
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={8} className="px-4 py-10 text-center text-muted-foreground">Nothing here</td></tr>}
          </tbody>
        </table>
      </Card>

      <Dialog open={!!rejectFor} onOpenChange={() => setRejectFor(null)}>
        <DialogContent data-testid="reject-dialog">
          <DialogHeader><DialogTitle className="font-heading">Reject {rejectFor?.quote_no}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Textarea data-testid="reject-reason-input" placeholder="Reason (kept on record)…" value={reason} onChange={(e) => setReason(e.target.value)} />
            <Button data-testid="reject-confirm-button" onClick={reject} variant="destructive" className="w-full">Reject Quotation</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
